import Hotjar from '@hotjar/browser';
import connectTheme from 'assets/branding/connectTheme';
import sodaTheme from 'assets/branding/sodaTheme';
import axios from 'axios';
import { Loader } from 'components/atoms/Loader/Loader';
import { PopupExistProcess } from 'components/molecules/Popup-exist-porcess/Popup-exist-porcess';
import { PopUpInformative } from 'components/molecules/Popup-informative/Popup-informative';
import { config } from 'config/constants';
import { defaultFirebaseAuth } from 'config/firebase-config';
import { signOut } from 'firebase/auth';
import ErrorBoundary from 'hooks/Error-boundary';
import { ScrollRestoration } from 'hooks/Scroll-restoration.hook';
import { IAppReducer } from 'models/reducer.interface';
import { setSignOut } from 'pages/sing-in/store/actions/auth.types';
import { Suspense, useEffect, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { AppRouter } from 'routes/routes';
import { setStateError } from 'store/actions/app.types';
import { setBranch } from 'store/actions/branch.types';
import { getBranch } from 'utils/branch.utils';
import { isConnect } from 'utils/branding.util';

const changeFavicon = (url: string) => {
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    (link as HTMLLinkElement).rel = 'icon';
    document.head.appendChild(link);
  }
  (link as HTMLLinkElement).href = url;
};

const App = () => {
  useEffect(() => {
    const root = document.documentElement;

    const applyTheme = (theme: Record<string, string>) => {
      for (const variable in theme) {
        if (Object.prototype.hasOwnProperty.call(theme, variable)) {
          root.style.setProperty(variable, theme[variable]);
        }
      }
    };

    const loadTheme = async () => {
      if (isConnect) {
        applyTheme(connectTheme);
        changeFavicon('/favicon.ico');
        document.title = 'Connect Assistance';
      } else {
        try {
          const { data } = await axios.get(
            `${config.merakiCms.url}/v2/data-store/theme_soda`
          );
          applyTheme(data);
        } catch (error) {
          console.warn(
            'Fallo la carga de tema desde Strapi. Usando fallback.',
            error
          );
          applyTheme(sodaTheme);
        }

        changeFavicon('/favicon-soda.ico');
        document.title = 'Soda seguros';
      }
    };

    loadTheme();
  }, []);

  const siteId = Number(config.hotjarId);
  const hotjarVersion = 6;

  Hotjar.init(siteId, hotjarVersion);
  const {
    branchReducer: { branch },
    authReducer: { accessToken, email, agentId },
    checkoutReducer: { email: emailCheckout, funelId },
    createAccountReducer: { email: emailCreateAccount },
  } = useSelector((state: IAppReducer) => state);
  const timeOfInactivity =
    1000 * 60 * Number(!agentId ? config.timer.timeOfInactivity : 240);
  const timeOfSession =
    1000 * 60 * Number(!agentId ? config.timer.timeOfSession : 250);
  const dispatch = useDispatch();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const country = params.get('country') as string;

  const handleInactivity = async () => {
    if (accessToken) {
      setShowAlertInactivity(true);
    }
  };
  const handleLogout = async () => {
    try {
      if (accessToken) {
        signOut(defaultFirebaseAuth);
        dispatch(setSignOut());
      }
    } catch (error) {
      // TODO Manejar error
    } finally {
      setShowAlertInactivity(false);
    }
  };
  const timer = useIdleTimer({
    timeout: timeOfInactivity,
    onIdle: handleInactivity,
    onAction: handleLogout,
    debounce: timeOfSession,
    startManually: true,
  });
  useEffect(() => {
    if (!branch && !accessToken && !country) {
      dispatch(setBranch(getBranch()));
    }
  }, [branch]);
  useEffect(() => {
    if (accessToken) {
      timer.reset();
      timer.start();
    }
  }, [accessToken]);
  const { error } = useSelector((state: IAppReducer) => state.appReducer);
  const [showAlertInactivity, setShowAlertInactivity] =
    useState<boolean>(false);

  const handlePopUp = () => {
    dispatch(setStateError(!error));
  };

  const handleClick = () => {
    window.open('https://wa.me/17879995050', '_blank');
  };

  return (
    <Suspense fallback={<Loader />}>
      <ErrorBoundary
        user={email || emailCheckout || emailCreateAccount}
        agentId={agentId}
        funelId={funelId}
      >
        <>
          <ScrollRestoration>
            <AppRouter></AppRouter>
          </ScrollRestoration>
          {error && (
            <PopUpInformative
              title="Error"
              text="La información ingresada no es correcta. Verifique los datos e intente nuevamente. Si necesita ayuda, puede contactar a nuestro equipo de atención al cliente."
              type="error"
              labelButton="solicitar soporte"
              onClickButton={handleClick}
              closePopUp={handlePopUp}
            ></PopUpInformative>
          )}
          {showAlertInactivity && (
            <PopupExistProcess
              title="La sesión expira pronto"
              message="Detectamos inactividad y por seguridad cerraremos la sesión pronto. ¿Deseas continuar navegando?"
              labelButton="Continuar"
              labelButtonCancel="Salir"
              type="warning"
              closePopUp={() => setShowAlertInactivity(false)}
              exitProcess={handleLogout}
            />
          )}
        </>
      </ErrorBoundary>
    </Suspense>
  );
};

export default App;
